/**
 * DatePicker Component
 * A date input component with calendar dropdown using DaisyUI styling
 * 
 * @description Provides a styled date picker with:
 * - Text input for direct date entry (format: DD-MMM-YYYY e.g., 27-Nov-2025)
 * - Dropdown calendar panel for visual date selection
 * - Year and month navigation
 * - Today button for quick selection
 * - Size and color variants
 * - Disabled and readonly states
 * - Min/max date constraints
 * - Localized month names
 * 
 * @example
 * ```typescript
 * const datePicker = new DatePicker(container, {
 *   name: 'birthDate',
 *   label: 'Date of Birth',
 *   value: new Date(1990, 0, 15),
 *   format: 'DD-MMM-YYYY',
 *   minDate: new Date(1900, 0, 1),
 *   maxDate: new Date(),
 *   onChange: (event) => console.log('Selected:', event.newValue)
 * });
 * await datePicker.initialize();
 * ```
 */

import { BaseFormField, FormFieldOptions, FieldSize, FieldColor, ValidationResult } from './IFormField';
import { ZIndex } from '../constants/ZIndex';

/**
 * Date format patterns supported
 */
export type DateFormat = 'DD MMM YYYY' | 'DD-MMM-YYYY' | 'DD/MM/YYYY' | 'MM/DD/YYYY' | 'YYYY-MM-DD' | 'DD.MM.YYYY' | 'DDD DD MMM YYYY';

/**
 * Month names for formatting
 */
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const MONTH_NAMES_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];

const DAY_NAMES_SHORT = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const DAY_NAMES_ABBREV = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * Options specific to DatePicker component
 */
export interface DatePickerOptions extends FormFieldOptions {
  /** Initial date value */
  value?: Date | null;
  /** Date format for display (default: DD MMM YYYY) */
  format?: DateFormat;
  /** Placeholder text when no date selected */
  placeholder?: string;
  /** Size variant */
  size?: FieldSize;
  /** Color variant for the input */
  color?: FieldColor;
  /** Minimum selectable date */
  minDate?: Date | null;
  /** Maximum selectable date */
  maxDate?: Date | null;
  /** First day of week (0=Sunday, 1=Monday) */
  firstDayOfWeek?: 0 | 1;
  /** Show today button */
  showTodayButton?: boolean;
  /** Show clear button */
  showClearButton?: boolean;
  /** Helper text shown below input */
  helperText?: string;
  /** Custom validation function */
  customValidation?: (date: Date | null) => ValidationResult;
}

/**
 * DatePicker form field component
 */
export class DatePicker extends BaseFormField<Date | null> {
  private inputEl: HTMLInputElement | null = null;
  private dropdownEl: HTMLElement | null = null;
  private calendarEl: HTMLElement | null = null;
  private isOpen = false;
  private viewDate: Date; // The month/year currently being viewed
  private datePickerOptions: DatePickerOptions;
  private documentClickHandler: ((e: MouseEvent) => void) | null = null;
  private scrollHandler: (() => void) | null = null;
  private resizeHandler: (() => void) | null = null;
  private uniqueId: string;

  constructor(container: HTMLElement, options: DatePickerOptions) {
    super(container, options, options.value ?? null);
    this.datePickerOptions = {
      format: 'DD MMM YYYY',
      placeholder: 'Select date...',
      firstDayOfWeek: 0,
      showTodayButton: true,
      showClearButton: true,
      ...options
    };
    this.viewDate = options.value ? new Date(options.value) : new Date();
    this.uniqueId = `datepicker-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Initialize the date picker component
   */
  async initialize(): Promise<void> {
    this.render();
    this.bindEvents();
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    if (this.documentClickHandler) {
      document.removeEventListener('click', this.documentClickHandler);
    }
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler, true);
    }
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
    }
    // Remove portal dropdown from body
    if (this.dropdownEl && this.dropdownEl.parentNode === document.body) {
      document.body.removeChild(this.dropdownEl);
    }
    this.container.innerHTML = '';
  }

  /**
   * Focus the input element
   */
  focus(): void {
    this.inputEl?.focus();
  }

  /**
   * Set disabled state
   */
  setDisabled(disabled: boolean): void {
    this.options.disabled = disabled;
    if (this.inputEl) {
      this.inputEl.disabled = disabled;
    }
    if (disabled) {
      this.close();
    }
  }

  /**
   * Set readonly state
   */
  setReadOnly(readonly: boolean): void {
    this.options.readonly = readonly;
    if (this.inputEl) {
      this.inputEl.readOnly = readonly;
    }
    if (readonly) {
      this.close();
    }
  }

  /**
   * Validate the current value
   */
  validate(): ValidationResult {
    // Check required
    const baseValidation = super.validate();
    if (!baseValidation.valid) return baseValidation;

    // Check min/max dates
    if (this.currentValue) {
      const { minDate, maxDate } = this.datePickerOptions;
      
      if (minDate && this.currentValue < this.normalizeDate(minDate)) {
        return {
          valid: false,
          message: `Date must be on or after ${this.formatDate(minDate)}`
        };
      }
      
      if (maxDate && this.currentValue > this.normalizeDate(maxDate)) {
        return {
          valid: false,
          message: `Date must be on or before ${this.formatDate(maxDate)}`
        };
      }
    }

    // Custom validation
    if (this.datePickerOptions.customValidation) {
      return this.datePickerOptions.customValidation(this.currentValue);
    }

    return { valid: true };
  }

  /**
   * Check if value is empty
   */
  protected isEmpty(): boolean {
    return this.currentValue === null;
  }

  /**
   * Update the display to match current value
   */
  protected updateDisplay(): void {
    if (this.inputEl) {
      this.inputEl.value = this.currentValue ? this.formatDate(this.currentValue) : '';
    }
    if (this.viewDate && this.currentValue) {
      this.viewDate = new Date(this.currentValue);
    }
  }

  /**
   * Open the calendar dropdown
   */
  open(): void {
    if (this.isOpen || this.options.disabled || this.options.readonly) return;
    
    this.isOpen = true;
    if (this.dropdownEl) {
      // Position the portal dropdown
      this.positionDropdown();
      this.dropdownEl.classList.remove('hidden');
      
      // Add scroll/resize listeners to reposition
      this.scrollHandler = () => this.positionDropdown();
      this.resizeHandler = () => this.positionDropdown();
      window.addEventListener('scroll', this.scrollHandler, true);
      window.addEventListener('resize', this.resizeHandler);
    }
    this.renderCalendar();
  }

  /**
   * Close the calendar dropdown
   */
  close(): void {
    if (!this.isOpen) return;
    
    this.isOpen = false;
    if (this.dropdownEl) {
      this.dropdownEl.classList.add('hidden');
    }
    
    // Remove scroll/resize listeners
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler, true);
      this.scrollHandler = null;
    }
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
      this.resizeHandler = null;
    }
  }

  /**
   * Toggle the calendar dropdown
   */
  toggle(): void {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  /**
   * Set minimum date constraint
   */
  setMinDate(date: Date | null): void {
    this.datePickerOptions.minDate = date;
    if (this.isOpen) {
      this.renderCalendar();
    }
  }

  /**
   * Set maximum date constraint
   */
  setMaxDate(date: Date | null): void {
    this.datePickerOptions.maxDate = date;
    if (this.isOpen) {
      this.renderCalendar();
    }
  }

  /**
   * Render the date picker HTML
   */
  private render(): void {
    const {
      name,
      label,
      placeholder,
      size = 'md',
      color,
      disabled,
      readonly,
      helperText,
      className = ''
    } = this.datePickerOptions;

    // Build size class
    const sizeClass = size !== 'md' ? `input-${size}` : '';
    
    // Build color class
    const colorClass = color ? `input-${color}` : '';

    // Build input classes
    const inputClasses = ['input', 'input-bordered', sizeClass, colorClass, 'w-full', 'pr-10', className]
      .filter(Boolean)
      .join(' ');

    // Generate unique ID
    const inputId = `datepicker-input-${this.uniqueId}`;

    // Build HTML structure (without dropdown - it's a portal now)
    const html = `
      <div class="form-control w-full">
        ${label ? `<label class="label" for="${inputId}"><span class="label-text font-medium">${label}</span></label>` : ''}
        <div class="relative" data-datepicker-anchor>
          <input 
            type="text" 
            id="${inputId}"
            name="${name}"
            class="${inputClasses}"
            placeholder="${placeholder}"
            value="${this.currentValue ? this.formatDate(this.currentValue) : ''}"
            ${disabled ? 'disabled' : ''}
            ${readonly ? 'readonly' : ''}
            autocomplete="off"
          />
          <button 
            type="button" 
            class="absolute right-2 top-1/2 -translate-y-1/2 btn btn-ghost btn-${size === 'xs' ? 'xs' : size === 'sm' ? 'xs' : 'sm'} px-2"
            tabindex="-1"
            ${disabled || readonly ? 'disabled' : ''}
          >
            <i class="fas fa-calendar-alt text-base-content/60"></i>
          </button>
        </div>
        ${helperText ? `<label class="label"><span class="label-text-alt text-base-content/70">${helperText}</span></label>` : ''}
      </div>
    `;

    this.container.innerHTML = html;

    // Cache DOM references
    this.inputEl = this.container.querySelector(`#${inputId}`);
    
    // Create portal dropdown (appended to body for proper z-index stacking)
    this.createPortalDropdown();
  }

  /**
   * Create the dropdown as a portal (appended to document.body)
   * This ensures it appears above modals and other stacking contexts
   */
  private createPortalDropdown(): void {
    // Create the dropdown element
    this.dropdownEl = document.createElement('div');
    this.dropdownEl.className = 'datepicker-portal hidden';
    this.dropdownEl.style.cssText = `
      position: fixed;
      z-index: ${ZIndex.MODAL_DROPDOWN};
    `;
    this.dropdownEl.innerHTML = `
      <div class="card bg-base-100 shadow-2xl border border-base-300 w-80 overflow-visible">
        <div class="card-body p-3 overflow-visible" data-calendar></div>
      </div>
    `;
    
    // Append to body
    document.body.appendChild(this.dropdownEl);
    
    // Cache calendar element reference
    this.calendarEl = this.dropdownEl.querySelector('[data-calendar]');
  }

  /**
   * Position the portal dropdown relative to the input
   */
  private positionDropdown(): void {
    if (!this.dropdownEl || !this.inputEl) return;
    
    const inputRect = this.inputEl.getBoundingClientRect();
    const dropdownHeight = this.dropdownEl.offsetHeight || 300; // Estimate if not rendered yet
    const viewportHeight = window.innerHeight;
    
    // Calculate if dropdown fits below or needs to go above
    const spaceBelow = viewportHeight - inputRect.bottom;
    const spaceAbove = inputRect.top;
    const fitsBelow = spaceBelow >= dropdownHeight + 8;
    
    if (fitsBelow || spaceBelow >= spaceAbove) {
      // Position below the input
      this.dropdownEl.style.top = `${inputRect.bottom + 4}px`;
    } else {
      // Position above the input
      this.dropdownEl.style.top = `${inputRect.top - dropdownHeight - 4}px`;
    }
    
    this.dropdownEl.style.left = `${inputRect.left}px`;
  }

  /**
   * Bind event listeners
   */
  private bindEvents(): void {
    if (!this.inputEl) return;

    const toggleButton = this.container.querySelector('button');

    // Toggle button click
    toggleButton?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.toggle();
    });

    // Input focus - open calendar
    this.inputEl.addEventListener('focus', () => {
      // Don't auto-open on focus, user can click the button
    });

    // Input blur - validate typed input
    this.inputEl.addEventListener('blur', () => {
      this.parseInputValue();
    });

    // Input keydown
    this.inputEl.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.parseInputValue();
        this.close();
      } else if (e.key === 'Escape') {
        this.close();
      } else if (e.key === 'ArrowDown' && !this.isOpen) {
        e.preventDefault();
        this.open();
      }
    });

    // Close on click outside (check both container and portal dropdown)
    this.documentClickHandler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const isInsideContainer = this.container.contains(target);
      const isInsideDropdown = this.dropdownEl?.contains(target);
      
      if (!isInsideContainer && !isInsideDropdown) {
        this.close();
      }
    };
    document.addEventListener('click', this.documentClickHandler);
  }

  /**
   * Parse the input value and update the date
   */
  private parseInputValue(): void {
    if (!this.inputEl) return;
    
    const inputValue = this.inputEl.value.trim();
    
    if (!inputValue) {
      if (this.currentValue !== null) {
        const oldValue = this.currentValue;
        this.currentValue = null;
        this.fireChangeEvent(oldValue, null);
      }
      return;
    }

    const parsedDate = this.parseDate(inputValue);
    
    if (parsedDate) {
      const oldValue = this.currentValue;
      this.currentValue = parsedDate;
      this.viewDate = new Date(parsedDate);
      this.inputEl.value = this.formatDate(parsedDate);
      
      if (!oldValue || oldValue.getTime() !== parsedDate.getTime()) {
        this.fireChangeEvent(oldValue, parsedDate);
      }
    } else {
      // Invalid date - restore previous value
      this.inputEl.value = this.currentValue ? this.formatDate(this.currentValue) : '';
    }
  }

  /**
   * Parse a date string based on format
   */
  private parseDate(str: string): Date | null {
    const format = this.datePickerOptions.format || 'DD MMM YYYY';
    
    // Try multiple formats
    const formats: DateFormat[] = [format, 'DD MMM YYYY', 'DD-MMM-YYYY', 'DDD DD MMM YYYY', 'DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD'];
    
    for (const fmt of formats) {
      const date = this.parseDateWithFormat(str, fmt);
      if (date) return date;
    }
    
    // Try native Date parsing as fallback
    const nativeDate = new Date(str);
    if (!isNaN(nativeDate.getTime())) {
      return this.normalizeDate(nativeDate);
    }
    
    return null;
  }

  /**
   * Parse date with specific format
   */
  private parseDateWithFormat(str: string, format: DateFormat): Date | null {
    const cleanStr = str.trim();
    
    try {
      switch (format) {
        case 'DD MMM YYYY': {
          // Match pattern like "08 Jan 1981"
          const match = cleanStr.match(/^(\d{1,2})\s+([A-Za-z]{3})\s+(\d{4})$/);
          if (match) {
            const day = parseInt(match[1], 10);
            const monthIndex = MONTH_NAMES_SHORT.findIndex(m => 
              m.toLowerCase() === match[2].toLowerCase()
            );
            const year = parseInt(match[3], 10);
            if (monthIndex >= 0 && day >= 1 && day <= 31) {
              return new Date(year, monthIndex, day);
            }
          }
          break;
        }
        case 'DD-MMM-YYYY': {
          const match = cleanStr.match(/^(\d{1,2})-([A-Za-z]{3})-(\d{4})$/);
          if (match) {
            const day = parseInt(match[1], 10);
            const monthIndex = MONTH_NAMES_SHORT.findIndex(m => 
              m.toLowerCase() === match[2].toLowerCase()
            );
            const year = parseInt(match[3], 10);
            if (monthIndex >= 0 && day >= 1 && day <= 31) {
              return new Date(year, monthIndex, day);
            }
          }
          break;
        }
        case 'DD/MM/YYYY': {
          const match = cleanStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
          if (match) {
            const day = parseInt(match[1], 10);
            const month = parseInt(match[2], 10) - 1;
            const year = parseInt(match[3], 10);
            if (month >= 0 && month <= 11 && day >= 1 && day <= 31) {
              return new Date(year, month, day);
            }
          }
          break;
        }
        case 'MM/DD/YYYY': {
          const match = cleanStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
          if (match) {
            const month = parseInt(match[1], 10) - 1;
            const day = parseInt(match[2], 10);
            const year = parseInt(match[3], 10);
            if (month >= 0 && month <= 11 && day >= 1 && day <= 31) {
              return new Date(year, month, day);
            }
          }
          break;
        }
        case 'YYYY-MM-DD': {
          const match = cleanStr.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
          if (match) {
            const year = parseInt(match[1], 10);
            const month = parseInt(match[2], 10) - 1;
            const day = parseInt(match[3], 10);
            if (month >= 0 && month <= 11 && day >= 1 && day <= 31) {
              return new Date(year, month, day);
            }
          }
          break;
        }
        case 'DD.MM.YYYY': {
          const match = cleanStr.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
          if (match) {
            const day = parseInt(match[1], 10);
            const month = parseInt(match[2], 10) - 1;
            const year = parseInt(match[3], 10);
            if (month >= 0 && month <= 11 && day >= 1 && day <= 31) {
              return new Date(year, month, day);
            }
          }
          break;
        }
        case 'DDD DD MMM YYYY': {
          // Match pattern like "Thu 27 Nov 2025"
          const match = cleanStr.match(/^([A-Za-z]{3})\s+(\d{1,2})\s+([A-Za-z]{3})\s+(\d{4})$/);
          if (match) {
            const day = parseInt(match[2], 10);
            const monthIndex = MONTH_NAMES_SHORT.findIndex(m => 
              m.toLowerCase() === match[3].toLowerCase()
            );
            const year = parseInt(match[4], 10);
            if (monthIndex >= 0 && day >= 1 && day <= 31) {
              return new Date(year, monthIndex, day);
            }
          }
          break;
        }
      }
    } catch {
      // Parsing failed
    }
    
    return null;
  }

  /**
   * Format a date according to the configured format
   */
  private formatDate(date: Date): string {
    const format = this.datePickerOptions.format || 'DD MMM YYYY';
    
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const monthShort = MONTH_NAMES_SHORT[date.getMonth()];
    const dayName = DAY_NAMES_ABBREV[date.getDay()];
    const year = date.getFullYear().toString();
    
    switch (format) {
      case 'DD MMM YYYY':
        return `${day} ${monthShort} ${year}`;
      case 'DD-MMM-YYYY':
        return `${day}-${monthShort}-${year}`;
      case 'DD/MM/YYYY':
        return `${day}/${month}/${year}`;
      case 'MM/DD/YYYY':
        return `${month}/${day}/${year}`;
      case 'YYYY-MM-DD':
        return `${year}-${month}-${day}`;
      case 'DD.MM.YYYY':
        return `${day}.${month}.${year}`;
      case 'DDD DD MMM YYYY':
        return `${dayName} ${day} ${monthShort} ${year}`;
      default:
        return `${day} ${monthShort} ${year}`;
    }
  }

  /**
   * Normalize a date to midnight (removes time component)
   */
  private normalizeDate(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  /**
   * Render the calendar panel
   */
  private renderCalendar(): void {
    if (!this.calendarEl) return;

    const { minDate, maxDate, showTodayButton, showClearButton, firstDayOfWeek } = this.datePickerOptions;
    
    const year = this.viewDate.getFullYear();
    const month = this.viewDate.getMonth();
    
    // Get first day of month and total days
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const totalDays = lastDay.getDate();
    
    // Calculate starting position (accounting for first day of week)
    let startDayOfWeek = firstDay.getDay();
    if (firstDayOfWeek === 1) {
      startDayOfWeek = startDayOfWeek === 0 ? 6 : startDayOfWeek - 1;
    }
    
    // Generate day headers
    const dayHeaders = firstDayOfWeek === 1 
      ? ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su']
      : DAY_NAMES_SHORT;
    
    // Generate year options (100 years back, 50 years forward)
    const currentYear = new Date().getFullYear();
    const yearOptions = [];
    for (let y = currentYear - 100; y <= currentYear + 50; y++) {
      yearOptions.push(`<option value="${y}" ${y === year ? 'selected' : ''}>${y}</option>`);
    }
    
    // Generate month options
    const monthOptions = MONTH_NAMES.map((name, idx) => 
      `<option value="${idx}" ${idx === month ? 'selected' : ''}>${name}</option>`
    ).join('');
    
    // Today's date for highlighting
    const today = this.normalizeDate(new Date());
    const selectedDate = this.currentValue ? this.normalizeDate(this.currentValue) : null;
    
    // Normalize min/max for comparison
    const minNorm = minDate ? this.normalizeDate(minDate) : null;
    const maxNorm = maxDate ? this.normalizeDate(maxDate) : null;
    
    // Generate calendar days
    let daysHtml = '';
    
    // Empty cells before first day
    for (let i = 0; i < startDayOfWeek; i++) {
      daysHtml += '<div class="w-8 h-8"></div>';
    }
    
    // Day cells
    for (let day = 1; day <= totalDays; day++) {
      const cellDate = new Date(year, month, day);
      const isToday = cellDate.getTime() === today.getTime();
      const isSelected = selectedDate && cellDate.getTime() === selectedDate.getTime();
      const isDisabled = (minNorm && cellDate < minNorm) || (maxNorm && cellDate > maxNorm);
      
      let dayClass = 'btn btn-sm btn-ghost w-8 h-8 p-0 min-h-0';
      
      if (isSelected) {
        dayClass = 'btn btn-sm btn-primary w-8 h-8 p-0 min-h-0';
      } else if (isToday) {
        dayClass = 'btn btn-sm btn-outline btn-primary w-8 h-8 p-0 min-h-0';
      }
      
      if (isDisabled) {
        dayClass += ' btn-disabled opacity-30';
      }
      
      daysHtml += `
        <button 
          type="button" 
          class="${dayClass}" 
          data-day="${day}"
          ${isDisabled ? 'disabled' : ''}
        >${day}</button>
      `;
    }
    
    // Build footer buttons
    const footerButtons = [];
    if (showTodayButton) {
      const todayDisabled = (minNorm && today < minNorm) || (maxNorm && today > maxNorm);
      footerButtons.push(`
        <button type="button" class="btn btn-ghost btn-xs" data-action="today" ${todayDisabled ? 'disabled' : ''}>
          Today
        </button>
      `);
    }
    if (showClearButton) {
      footerButtons.push(`
        <button type="button" class="btn btn-ghost btn-xs" data-action="clear">
          Clear
        </button>
      `);
    }
    
    this.calendarEl.innerHTML = `
      <!-- Header with month/year navigation -->
      <div class="flex items-center justify-between mb-3">
        <button type="button" class="btn btn-ghost btn-sm btn-square" data-action="prev-month">
          <i class="fas fa-chevron-left text-xs"></i>
        </button>
        <div class="flex gap-2">
          <select class="select select-ghost select-sm min-h-0 h-8 w-28 pr-8" style="background-position-y: calc(50% - 2px);" data-select="month">
            ${monthOptions}
          </select>
          <select class="select select-ghost select-sm min-h-0 h-8 w-20 pr-8" style="background-position-y: calc(50% - 2px);" data-select="year">
            ${yearOptions.join('')}
          </select>
        </div>
        <button type="button" class="btn btn-ghost btn-sm btn-square" data-action="next-month">
          <i class="fas fa-chevron-right text-xs"></i>
        </button>
      </div>
      
      <!-- Day headers -->
      <div class="grid grid-cols-7 gap-1 mb-1">
        ${dayHeaders.map(d => `<div class="w-8 h-6 flex items-center justify-center text-xs font-semibold text-base-content/60">${d}</div>`).join('')}
      </div>
      
      <!-- Calendar days -->
      <div class="grid grid-cols-7 gap-1">
        ${daysHtml}
      </div>
      
      <!-- Footer buttons -->
      ${footerButtons.length > 0 ? `
        <div class="flex justify-center gap-2 mt-2 pt-2 border-t border-base-300">
          ${footerButtons.join('')}
        </div>
      ` : ''}
    `;
    
    // Bind calendar events
    this.bindCalendarEvents();
  }

  /**
   * Bind calendar panel events
   */
  private bindCalendarEvents(): void {
    if (!this.calendarEl) return;
    
    // Previous month
    this.calendarEl.querySelector('[data-action="prev-month"]')?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.viewDate.setMonth(this.viewDate.getMonth() - 1);
      this.renderCalendar();
    });
    
    // Next month
    this.calendarEl.querySelector('[data-action="next-month"]')?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.viewDate.setMonth(this.viewDate.getMonth() + 1);
      this.renderCalendar();
    });
    
    // Month select
    this.calendarEl.querySelector('[data-select="month"]')?.addEventListener('change', (e) => {
      const target = e.target as HTMLSelectElement;
      this.viewDate.setMonth(parseInt(target.value, 10));
      this.renderCalendar();
    });
    
    // Year select
    this.calendarEl.querySelector('[data-select="year"]')?.addEventListener('change', (e) => {
      const target = e.target as HTMLSelectElement;
      this.viewDate.setFullYear(parseInt(target.value, 10));
      this.renderCalendar();
    });
    
    // Today button
    this.calendarEl.querySelector('[data-action="today"]')?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.selectDate(new Date());
    });
    
    // Clear button
    this.calendarEl.querySelector('[data-action="clear"]')?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const oldValue = this.currentValue;
      this.currentValue = null;
      this.updateDisplay();
      this.close();
      if (oldValue !== null) {
        this.fireChangeEvent(oldValue, null);
      }
    });
    
    // Day buttons
    this.calendarEl.querySelectorAll('[data-day]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const day = parseInt((btn as HTMLElement).dataset.day || '1', 10);
        const selectedDate = new Date(this.viewDate.getFullYear(), this.viewDate.getMonth(), day);
        this.selectDate(selectedDate);
      });
    });
  }

  /**
   * Select a date
   */
  private selectDate(date: Date): void {
    const normalizedDate = this.normalizeDate(date);
    const oldValue = this.currentValue;
    
    this.currentValue = normalizedDate;
    this.viewDate = new Date(normalizedDate);
    this.updateDisplay();
    this.close();
    
    if (!oldValue || oldValue.getTime() !== normalizedDate.getTime()) {
      this.fireChangeEvent(oldValue, normalizedDate);
    }
  }

  /**
   * Get the date as an ISO string (YYYY-MM-DD)
   */
  getISODate(): string | null {
    if (!this.currentValue) return null;
    return this.formatDate(this.currentValue).replace(/(\d{2})-([A-Za-z]{3})-(\d{4})/, (_, d, m, y) => {
      const monthIndex = MONTH_NAMES_SHORT.findIndex(mn => mn === m);
      const month = (monthIndex + 1).toString().padStart(2, '0');
      return `${y}-${month}-${d}`;
    });
  }

  /**
   * Set date from an ISO string (YYYY-MM-DD)
   */
  setFromISODate(isoDate: string, silent: boolean = false): void {
    const date = this.parseDateWithFormat(isoDate, 'YYYY-MM-DD');
    if (date) {
      this.setValue(date, silent);
    }
  }
}
